import ScheduleTable from "./ScheduleTable"
import { forwardRef, useEffect, useMemo, useRef, useState } from "react"
import { DaysInWeekLabel } from "./Location"
import { GetSchedulesInterface, SelectedSchedules } from "@type/schedules.types"
import { GetLocationDetailsInterface, InactiveShift } from "@type/locations.types"
import { ScheduleStatus } from "../Constants/schedulesConstants"
import { round } from "lodash"
import { calculateTimeDifferenceWrtDst } from "config/utils"
import useFeatureFlags from "../../../hooks/useFeatureFlags"
import { FeatureFlagEnums } from "../../../types/featureFlags.type"
import useSchedulesQueryParams from "hooks/useSchedulesQueryParams"
import { DATE_FORMATS } from "config/constants"
import moment from "moment"
import { ShiftInterface } from "@type/workMonitor.types"
import { SDEventTypes } from "@type/staticData.types"

interface ColumnHeaderProps {
	label: string
	key: string
	shifts: number
	hours: number
}

const createSelectedCellKey = (rowKeyValue: string, colKeyValue: string, rowIndex: number, colIndex: number) =>
	`${rowKeyValue}_${colKeyValue}_${rowIndex}_${colIndex}`

interface Props {
	data: (GetSchedulesInterface | InactiveShift)[]
	rowHeaderKey: string
	columnHeaderKey: string
	rowHeaderLabels: { [key: string]: string }[]
	columnHeaderLabels: DaysInWeekLabel[]
	noDataText?: string
	multipleSingleSelection?: boolean
	selected: (selectedCell: SelectedSchedules) => void
	// externalSelectedCells?: SelectedSchedules
}
const ScheduleRevamp: React.FC<Props & { ref: React.Ref<() => void> }> = forwardRef((props, ref: any) => {
	const {
		data,
		rowHeaderKey,
		columnHeaderKey,
		rowHeaderLabels,
		columnHeaderLabels,
		noDataText = "",
		multipleSingleSelection = false,
		selected,
		// externalSelectedCells,
	} = props

	const cellCoordinates = useRef<{ rIndex: number; cIndex: number } | null>(null)
	const dataHeaderKeyRowIndex = useRef<string[]>([])
	const selectedCellsRef = useRef<number>(0)
	const [selectedCells, setSelectedCells] = useState<SelectedSchedules>({})
	const { featureFlagEnabled } = useFeatureFlags()
	const respectDst = featureFlagEnabled(FeatureFlagEnums.RESPECT_DST)

	const { schedule: paramSchedule, setSearchParams } = useSchedulesQueryParams()

	useEffect(() => {
		ref.current = resetState
	}, [ref])

	// Helper: Transform a raw schedule object into the format expected by the table selection/cells state.
	const transformScheduleToRecord = (schedule: any): any => {
		// const postId = typeof schedule.post === "object" ? schedule.post?._id : (schedule.post as string)
		const employeeId = schedule.employee && typeof schedule.employee === "object" ? schedule.employee?._id : ""

		return {
			...schedule,
			post: { _id: schedule.post?._id ?? schedule.post, name: schedule.post?.name ?? "" },
			employee: employeeId || "",
			isDeleted: !!schedule.deletedAt,
			updatedAt: "",
			onMouseDown: () => undefined,
			onMouseDrag: () => undefined,
			site: schedule.site as Partial<GetLocationDetailsInterface>,
			status: (schedule.status as string) || "",
			shift: (schedule.shift || {}) as ShiftInterface,
			user: "user" in schedule ? (schedule as any).user : "",
			eventType: (schedule.eventType || {}) as Partial<SDEventTypes>,
		}
	}

	// e9562e1b-7054-4bd8-8b21-c787fc7e6704_16-10-2025_2_3

	// INITIAL
	useEffect(() => {
		if (data.length && !Object.keys(selectedCells).length && paramSchedule) {
			const [postId, dateStr] = paramSchedule.split("_")
			const schedule = data.find(schedule => {
				const schedulePostId =
					typeof schedule.post === "object" ? schedule.post?._id : (schedule.post as string)
				const scheduleDate = moment(schedule.startTime).format(DATE_FORMATS.DD_MM_YYYY)
				return schedulePostId === postId && scheduleDate === dateStr
			})
			if (schedule) {
				const transformed = transformScheduleToRecord(schedule)
				setSelectedCells({ [paramSchedule]: transformed })
				selected({ [paramSchedule]: transformed })
			}
		}
	}, [data, paramSchedule])

	useEffect(() => {
		selected(selectedCells)
	}, [selectedCells])

	// useEffect(() => {
	// 	if (externalSelectedCells && Object.keys(externalSelectedCells).length > 0) {
	// 		const hasNewKeys = Object.keys(externalSelectedCells).some(key => !selectedCells[key])
	// 		if (hasNewKeys) {
	// 			setSelectedCells(externalSelectedCells)
	// 			selectedCellsRef.current = Object.keys(externalSelectedCells).length
	// 		}
	// 	}
	// }, [externalSelectedCells])

	const columns: ColumnHeaderProps[] = useMemo(() => {
		if (columnHeaderLabels.length) {
			const dayWiseScheduleStats = data.reduce((acc: { [key: string]: any }, curr) => {
				const hours =
					calculateTimeDifferenceWrtDst(curr.startTime, curr.finishTime, "seconds", respectDst) / 3600
				if (curr.status !== ScheduleStatus.INACTIVE && "deletedAt" in curr && !curr.deletedAt) {
					if (!acc[curr.date]) {
						acc[curr.date] = { shifts: 1, hours }
					} else {
						acc[curr.date].shifts++
						acc[curr.date].hours += hours
					}
				}
				return acc
			}, {})
			return columnHeaderLabels?.map(column => ({
				label: column.label,
				key: column.date,
				disable: column.disable,
				shifts: round(dayWiseScheduleStats[column.date]?.shifts, 2) || 0,
				hours: round(dayWiseScheduleStats[column.date]?.hours, 2) || 0,
			}))
		}
		return []
	}, [columnHeaderLabels, data])

	const resetState = () => {
		setSelectedCells({})
		selectedCellsRef.current = 0
	}

	const onMouseDrag = (drag: any, rowEndIndex: any, colEndIndex: any) => {
		if (drag) {
			let rowStartIndex: number
			let columnStartIndex: number

			// On mouse down will give first and last selected row and col index
			if (cellCoordinates.current) {
				rowStartIndex = cellCoordinates.current.rIndex
				columnStartIndex = cellCoordinates.current.cIndex

				if (rowStartIndex > rowEndIndex) [rowStartIndex, rowEndIndex] = [rowEndIndex, rowStartIndex]

				if (columnStartIndex > colEndIndex) [columnStartIndex, colEndIndex] = [colEndIndex, columnStartIndex]
			}

			// Extracting selected posts based selected drag coordinates
			const totalRows = [...dataHeaderKeyRowIndex.current].filter(id => {
				const rowNumber = +id.split("_")[1]
				return rowNumber >= rowStartIndex && rowNumber <= rowEndIndex
			})

			// Resetting state when new drag happens
			resetState()

			totalRows.forEach(row => {
				const splitSelectedHeader = row.split("_")
				const selectedPost = splitSelectedHeader[0]
				const selectedRowIndex = +splitSelectedHeader[1]
				for (let i = columnStartIndex; i <= colEndIndex; i++) {
					const data = rows[selectedPost][selectedRowIndex][i]

					setSelectedCells(prev => {
						const addCells = {
							...prev,
							[createSelectedCellKey(selectedPost, columns[i].key, selectedRowIndex, i)]: data["_id"]
								? { ...data, employee: data?.employee?._id ?? null }
								: null,
						}
						selectedCellsRef.current = Object.keys(addCells).length
						return addCells
					})
				}
			})
		}
	}

	const onMouseDown = ({ ctrlKey, metaKey }: MouseEvent, rowIndex: number, colIndex: number, _data: any) => {
		// Single Selection don't let select multiple cells
		/*if ((!ctrlKey || !metaKey) && selectedCellsRef.current > 1) {
				setSelectedCells({})
			}*/

		cellCoordinates.current = { rIndex: rowIndex, cIndex: colIndex }
		const key = createSelectedCellKey(_data[rowHeaderKey], _data[columnHeaderKey], rowIndex, colIndex)
		if (paramSchedule && paramSchedule === key) {
			setSearchParams({ schedule: "", employee: "", employee_branch: "", supplier: "" })
		} else {
			const [postId, dateStr] = key.split("_")
			const schedule = data.find(schedule => {
				const schedulePostId =
					typeof schedule.post === "object" ? schedule.post?._id : (schedule.post as string)
				const scheduleDate = moment(schedule.startTime).format(DATE_FORMATS.DD_MM_YYYY)
				return schedulePostId === postId && scheduleDate === dateStr
			})
			const employee = schedule?.employee?._id

			setSearchParams({ schedule: key, employee })
		}

		setSelectedCells(prev => {
			if (multipleSingleSelection) {
				selectedCellsRef.current = 1
				if (key in prev) {
					delete prev[key]
					const addCells = { ...prev }
					return addCells
				} else {
					const addCells = {
						...prev,
						[key]: _data["_id"] ? { ..._data, employee: _data?.employee?._id ?? null } : null,
					}
					return addCells
				}
			} else {
				if (ctrlKey || metaKey) {
					selectedCellsRef.current = 1
					if (key in prev) {
						delete prev[key]
						const addCells = { ...prev }
						return addCells
					} else {
						const addCells = {
							...prev,
							[key]: _data["_id"] ? { ..._data, employee: _data?.employee?._id ?? null } : null,
						}
						return addCells
					}
				} else {
					return {
						[key]: _data["_id"] ? { ..._data, employee: _data?.employee?._id ?? null } : null,
					}
				}
			}
		})
	}

	const rows = useMemo(() => {
		let rowIndex: string[] = []
		let rowCount = 0
		const dataFormat: any = {}
		const noOfSchedulesInSinglePost: { [key: string]: number } = {}

		rowHeaderLabels.forEach(rowHeader => {
			const rowKey = rowHeader[rowHeaderKey]
			let columnValueLength = 0
			columnHeaderLabels.forEach(columnHeader => {
				const columnKey = columnHeader.date
				const matchedData = [] as any
				data.forEach((row: any) => {
					// Extracting data that matches row and column key
					if (row[rowHeaderKey]["_id"] === rowKey && row[columnHeaderKey] === columnKey) {
						matchedData.push({ onMouseDown, onMouseDrag, ...row, post: row.post._id })
					}
				})
				if (columnValueLength < matchedData.length) columnValueLength = matchedData.length
				dataFormat[rowKey] = { ...dataFormat[rowKey], [columnKey]: matchedData }
			})
			noOfSchedulesInSinglePost[rowKey] = columnValueLength
		})
		const newDataFormat = {} as any

		for (const [dataKey, maxDataLength] of Object.entries(noOfSchedulesInSinglePost)) {
			if (maxDataLength === 0) {
				rowIndex = [...new Set([...rowIndex, `${dataKey}_${rowCount}`])]

				newDataFormat[dataKey] = {
					...newDataFormat[dataKey],
					[rowCount]: [
						...columnHeaderLabels.map(columnHeader => ({
							onMouseDown,
							onMouseDrag,
							[rowHeaderKey]: dataKey,
							[columnHeaderKey]: columnHeader.date,
							disable: columnHeader.disable,
						})),
					],
				}
				rowCount++
			} else {
				for (let i = 0; i < maxDataLength; i++) {
					const rowColumnValue = [] as any
					columnHeaderLabels.forEach((columnHeader: any) => {
						const columnKey = columnHeader[columnHeaderKey]
						rowColumnValue.push(
							dataFormat[dataKey][columnKey][i]
								? dataFormat[dataKey][columnKey][i]
								: {
										onMouseDown,
										onMouseDrag,
										[rowHeaderKey]: dataKey,
										[columnHeaderKey]: columnHeader[columnHeaderKey],
										disable: columnHeader.disable,
								  },
						)
					})

					rowIndex = [...new Set([...rowIndex, `${dataKey}_${rowCount}`])]

					newDataFormat[dataKey] = {
						...newDataFormat[dataKey],
						[rowCount]: [...rowColumnValue],
					}
					rowCount++
				}
			}
			dataHeaderKeyRowIndex.current = rowIndex
		}
		return newDataFormat
	}, [data, rowHeaderLabels, columnHeaderLabels])

	return (
		<ScheduleTable
			selectedCells={selectedCells}
			noDataText={noDataText}
			columns={columns}
			rows={rows}
			showRowHeaders
			rowHeaders={rowHeaderLabels}
		/>
	)
})
export default ScheduleRevamp
